import { DropOverlay } from "@/components/common/DropOverlay/DropOverlay"
import { EmptyState } from "@/components/common/EmptyState/EmptyState"
import { toast } from "@/components/common/Toast"
import { Typography } from "@/components/common/Typography/Typography"
import { MainLayout } from "@/components/layout/MainLayout/MainLayout"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import {
  getDataRoomDownloadUrl,
  useCreateFolder,
  useDataRoomTree,
  useDeleteDataRoomFile,
  useDeleteFolder,
  useUpdateDataRoomFileNote,
  useUpdateFolder,
} from "@/services/api/dataRoomService"
import {
  dataRoomFormats,
  useUploadFormats,
} from "@/services/api/uploadFormatsService"
import type { DataRoomFile, DataRoomFolder } from "@/types/dataRoom"
import { isLimitError } from "@/utils/limitErrors"
import { IconAlertTriangle, IconUpload } from "@tabler/icons-react"
import { useCallback, useMemo, useRef, useState } from "react"
import { useSearchParams } from "react-router"
import { DataRoomChecklistCard } from "./DataRoomChecklistCard"
import { DataRoomDropzone } from "./DataRoomDropzone"
import { DataRoomFileList } from "./DataRoomFileList"
import {
  DataRoomFolderDialogs,
  DataRoomFolderGrid,
  type FolderDialogState,
} from "./DataRoomFolderGrid"
import { DataRoomFolderHeader } from "./DataRoomFolderHeader"
import { DataRoomPreviewModal } from "./DataRoomPreviewModal"
import { DataRoomSummary } from "./DataRoomSummary"
import { DataRoomUploadTargetDialog } from "./DataRoomUploadTargetDialog"
import { previewKind } from "./previewKind"
import type { HandleFilesOptions } from "./useDataRoomUpload"
import {
  useDataRoomUpload,
  type DataRoomUploadEntry,
} from "./useDataRoomUpload"
import { DataRoomWelcome } from "./welcome/DataRoomWelcome"
import { useDataRoomWelcome } from "./welcome/useDataRoomWelcome"

const UPLOAD_ERROR_CODE_KEYS: Partial<Record<string, TranslationKeys>> = {
  DATA_ROOM_ALREADY_SUPERSEDED: "DATA_ROOM/ERROR_ALREADY_SUPERSEDED",
  DATA_ROOM_REVISION_SINGLE_FILE: "DATA_ROOM/ERROR_REVISION_SINGLE_FILE",
  DATA_ROOM_FILE_TOO_LARGE: "DATA_ROOM/ERROR_FILE_TOO_LARGE",
  UNSUPPORTED_FILE_TYPE: "DATA_ROOM/ERROR_UNSUPPORTED_FILE_TYPE",
  UPLOAD_FAILED: "DATA_ROOM/ERROR_UPLOAD_FAILED",
  DATA_ROOM_TOO_MANY_FILES: "DATA_ROOM/ERROR_TOO_MANY_FILES",
}

const FOLDER_ERROR_CODE_KEYS: Partial<Record<string, TranslationKeys>> = {
  DATA_ROOM_FOLDER_NOT_EMPTY: "DATA_ROOM/ERROR_FOLDER_NOT_EMPTY",
  DATA_ROOM_FOLDER_EXISTS: "DATA_ROOM/ERROR_FOLDER_EXISTS",
  DATA_ROOM_FOLDER_PROTECTED: "DATA_ROOM/ERROR_FOLDER_PROTECTED",
  DATA_ROOM_FOLDER_TOO_DEEP: "DATA_ROOM/ERROR_FOLDER_TOO_DEEP",
}

const DELETE_FILE_ERROR_CODE_KEYS: Partial<Record<string, TranslationKeys>> = {
  DATA_ROOM_FILE_PROTECTED: "DATA_ROOM/ERROR_FILE_PROTECTED",
  DATA_ROOM_ALREADY_ACCEPTED: "DATA_ROOM/ERROR_FILE_ALREADY_ACCEPTED",
}

const NOTE_ERROR_CODE_KEYS: Partial<Record<string, TranslationKeys>> = {
  DATA_ROOM_ALREADY_ACCEPTED: "DATA_ROOM/ERROR_FILE_ALREADY_ACCEPTED",
}

const LOCALE_BY_LANGUAGE: Record<string, string> = {
  DE: "de-DE",
  EN: "en-GB",
  JA: "ja-JP",
}

/** Reads a 4xx business error's `detail.code` off an axios-shaped error. */
function getApiErrorCode(error: unknown): string | null {
  const detail = (error as { response?: { data?: { detail?: unknown } } })
    ?.response?.data?.detail
  if (typeof detail !== "object" || detail === null) return null
  const code = (detail as { code?: unknown }).code
  return typeof code === "string" ? code : null
}

// Human-readable format groups, in display order. Proper names stay as they
// are; only the generic groups (text, images) are translated.
const FORMAT_GROUPS: Array<{
  extensions: string[]
  label: string | { key: TranslationKeys }
}> = [
  { extensions: [".pdf"], label: "PDF" },
  { extensions: [".doc", ".docx"], label: "Word" },
  { extensions: [".xls", ".xlsx", ".xlsm"], label: "Excel" },
  { extensions: [".ppt", ".pptx"], label: "PowerPoint" },
  { extensions: [".csv"], label: "CSV" },
  {
    extensions: [".txt", ".md"],
    label: { key: "DATA_ROOM/FORMAT_GROUP_TEXT" },
  },
  {
    extensions: [".png", ".jpg", ".jpeg"],
    label: { key: "DATA_ROOM/FORMAT_GROUP_IMAGES" },
  },
  { extensions: [".zip"], label: "ZIP" },
]

export const DataRoomPage: React.FC = () => {
  const { translate, currentLanguage } = useTranslation()
  const locale = LOCALE_BY_LANGUAGE[currentLanguage] ?? "de-DE"
  const {
    data: tree,
    isPending,
    isError,
    isSuccess,
    refetch,
  } = useDataRoomTree()
  const headingRef = useRef<HTMLHeadingElement>(null)
  const checklistRef = useRef<HTMLButtonElement>(null)
  const [checklistPulse, setChecklistPulse] = useState(0)
  // The open folder lives in the URL so browser back, reload and shared
  // links all land on the same view.
  const [searchParams, setSearchParams] = useSearchParams()
  const requestedFolderId = searchParams.get("folder")
  const [editingNoteFileId, setEditingNoteFileId] = useState<string | null>(
    null,
  )
  const setSelectedFolderId = useCallback(
    (folderId: string | null) => {
      // The file list is folder-scoped, so a row's editor would otherwise
      // vanish with unsaved text on navigation and silently reappear on
      // return — closing it here keeps editor state strictly per-row, as
      // intended.
      setEditingNoteFileId(null)
      setSearchParams(folderId ? { folder: folderId } : {})
    },
    [setSearchParams],
  )
  const [previewFile, setPreviewFile] = useState<DataRoomFile | null>(null)
  const [fileToDelete, setFileToDelete] = useState<DataRoomFile | null>(null)
  const [folderDialog, setFolderDialog] = useState<FolderDialogState | null>(
    null,
  )
  const fileInputRef = useRef<HTMLInputElement>(null)
  const revisionInputRef = useRef<HTMLInputElement>(null)
  // Where the next picked files go. Set right before the native picker
  // opens (root destination chooser, or a revision of one file) and cleared
  // once the picker hands the files back.
  const pickerTargetRef = useRef<HandleFilesOptions | null>(null)
  // Root uploads ask for a destination first: `files` holds a drop that is
  // waiting for the answer, `null` means the picker opens afterwards.
  const [uploadTarget, setUploadTarget] = useState<{
    files: File[] | null
  } | null>(null)
  // Unsaved note text per file, so a folder change never eats a draft.
  const [noteDrafts, setNoteDrafts] = useState<Map<string, string>>(
    () => new Map(),
  )

  const folders = useMemo(() => tree?.folders ?? [], [tree])
  const files = useMemo(() => tree?.files ?? [], [tree])
  // A stale folder id (deleted, or someone else's) quietly falls back to
  // the root once the tree is known.
  const selectedFolderId = useMemo(() => {
    if (!requestedFolderId) return null
    if (!tree) return requestedFolderId
    return folders.some((f) => f.id === requestedFolderId)
      ? requestedFolderId
      : null
  }, [requestedFolderId, tree, folders])

  const { entries, handleFiles, retry, dismiss } = useDataRoomUpload(
    selectedFolderId,
    files,
    refetch,
  )

  const uploadFormats = useUploadFormats()
  const acceptedExtensions = dataRoomFormats(uploadFormats).extensions
  const acceptAttribute = acceptedExtensions.join(", ")

  const formatsLabel = useMemo(() => {
    if (acceptedExtensions.length === 0) return undefined
    const accepted = new Set(acceptedExtensions.map((e) => e.toLowerCase()))
    const groups = FORMAT_GROUPS.filter((g) =>
      g.extensions.some((e) => accepted.has(e)),
    ).map((g) =>
      typeof g.label === "string" ? g.label : translate(g.label.key),
    )
    return translate("DATA_ROOM/FORMATS_SUPPORTED", {
      formats: groups.join(", "),
    })
  }, [acceptedExtensions, translate])

  const { mutate: createFolder } = useCreateFolder()
  const { mutate: updateFolder } = useUpdateFolder()
  const { mutate: deleteFolder } = useDeleteFolder()
  const { mutate: deleteFile, isPending: isDeletingFile } =
    useDeleteDataRoomFile()
  const { mutate: updateFileNote, isPending: isSavingNote } =
    useUpdateDataRoomFileNote()

  const handleMutationError = useCallback(
    (
      error: unknown,
      codeMap: Partial<Record<string, TranslationKeys>>,
      genericKey: TranslationKeys,
    ) => {
      // A limit or trial error already got the interceptor's upgrade toast
      if (isLimitError(error)) return
      const code = getApiErrorCode(error)
      const key = code ? codeMap[code] : undefined
      toast.error(translate(key ?? genericKey))
    },
    [translate],
  )

  /* ----------------------------- derived ----------------------------- */

  const childrenByParentId = useMemo(() => {
    const map = new Map<string | null, DataRoomFolder[]>()
    for (const folder of folders) {
      const list = map.get(folder.parentId) ?? []
      list.push(folder)
      map.set(folder.parentId, list)
    }
    return map
  }, [folders])

  const fileCountByFolderId = useMemo(() => {
    const counts = new Map<string, number>()
    for (const file of files) {
      // Cards count what the customer put there; turnus templates are
      // context, not progress (the summary line excludes them the same way).
      if (!file.folderId || file.isTurnusUpload) continue
      counts.set(file.folderId, (counts.get(file.folderId) ?? 0) + 1)
    }
    return counts
  }, [files])

  const hasContent = useCallback(
    (folderId: string) =>
      (fileCountByFolderId.get(folderId) ?? 0) > 0 ||
      (childrenByParentId.get(folderId)?.length ?? 0) > 0,
    [fileCountByFolderId, childrenByParentId],
  )

  const folderNameById = useMemo(() => {
    const map = new Map<string, string>()
    for (const folder of folders) map.set(folder.id, folder.name)
    return map
  }, [folders])

  const selectedFolder = useMemo(
    () => folders.find((f) => f.id === selectedFolderId) ?? null,
    [folders, selectedFolderId],
  )

  const breadcrumb = useMemo(() => {
    if (!selectedFolder) return []
    const chain: DataRoomFolder[] = []
    // A concurrent pair of folder moves can (briefly) leave two folders each
    // other's ancestor, which would otherwise spin this traversal forever.
    // Once a folder id is seen twice, the chain is cyclic — fall back to
    // showing just the folder itself instead of looping.
    const visited = new Set<string>()
    let current: DataRoomFolder | null = selectedFolder
    while (current) {
      if (visited.has(current.id)) return [selectedFolder]
      visited.add(current.id)
      chain.unshift(current)
      const parentId: string | null = current.parentId
      current = parentId
        ? (folders.find((f) => f.id === parentId) ?? null)
        : null
    }
    return chain
  }, [selectedFolder, folders])

  const visibleFolders = childrenByParentId.get(selectedFolderId) ?? []
  const filesInView = useMemo(
    () => files.filter((f) => f.folderId === selectedFolderId),
    [files, selectedFolderId],
  )
  // In-flight rows follow the folder they target; the root shows everything
  // so nothing an upload is doing can hide behind navigation.
  const uploadsInView = useMemo(
    () =>
      selectedFolderId === null
        ? entries
        : entries.filter((e) => e.folderId === selectedFolderId),
    [entries, selectedFolderId],
  )

  const isRoot = selectedFolderId === null
  const rootFolders = useMemo(
    () => childrenByParentId.get(null) ?? [],
    [childrenByParentId],
  )
  const checklist = useMemo(
    () => files.find((f) => f.isTurnusUpload && f.isChecklist) ?? null,
    [files],
  )
  // Root sections. Templates and the customer's own files never share a
  // list: one is turnus's material to download, the other is what the
  // customer handed in. Files turnus sent back are pulled up from every
  // folder so nothing actionable hides behind navigation.
  const rootTemplates = useMemo(
    () => filesInView.filter((f) => f.isTurnusUpload && f.id !== checklist?.id),
    [filesInView, checklist],
  )
  const revisionFiles = useMemo(
    () =>
      files.filter(
        (f) =>
          !f.isTurnusUpload &&
          f.status === "changes_requested" &&
          !f.supersededById,
      ),
    [files],
  )
  const rootCustomerFiles = useMemo(
    () =>
      filesInView.filter(
        (f) => !f.isTurnusUpload && f.status !== "changes_requested",
      ),
    [filesInView],
  )
  const hasListContent = isRoot
    ? rootCustomerFiles.length > 0 ||
      rootTemplates.length > 0 ||
      revisionFiles.length > 0 ||
      uploadsInView.length > 0
    : filesInView.length > 0 || uploadsInView.length > 0

  const welcome = useDataRoomWelcome({
    treeSucceeded: isSuccess,
    hasFolderParam: searchParams.has("folder"),
    busy:
      entries.some((entry) => entry.status === "uploading") ||
      previewFile !== null ||
      fileToDelete !== null ||
      folderDialog !== null ||
      uploadTarget !== null ||
      editingNoteFileId !== null,
  })

  /* ----------------------------- actions ----------------------------- */

  const openFilePicker = useCallback(() => {
    // At the root the files need a home first; inside a folder they already
    // have one.
    if (selectedFolderId === null && rootFolders.length > 0) {
      setUploadTarget({ files: null })
      return
    }
    // Overwrite the shared target unconditionally, even if a revision pick
    // was cancelled (no change event, so its target never got cleared) —
    // this input never supersedes a file.
    pickerTargetRef.current = { folderId: selectedFolderId }
    fileInputRef.current?.click()
  }, [selectedFolderId, rootFolders.length])

  const handleFileInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const picked = Array.from(event.target.files ?? [])
      const target = pickerTargetRef.current
      pickerTargetRef.current = null
      if (picked.length) {
        handleFiles(
          target?.supersedesFileId ? picked.slice(0, 1) : picked,
          target ?? undefined,
        )
      }
      event.target.value = ""
    },
    [handleFiles],
  )

  const chooseUploadTarget = useCallback(
    (folderId: string | null) => {
      const pending = uploadTarget?.files ?? null
      setUploadTarget(null)
      if (pending) {
        handleFiles(pending, { folderId })
        return
      }
      pickerTargetRef.current = { folderId }
      fileInputRef.current?.click()
    },
    [uploadTarget, handleFiles],
  )

  const handleDroppedFiles = useCallback(
    (dropped: File[]) => {
      if (!dropped.length) return
      if (selectedFolderId === null && rootFolders.length > 0) {
        setUploadTarget({ files: dropped })
        return
      }
      handleFiles(dropped)
    },
    [selectedFolderId, rootFolders.length, handleFiles],
  )

  const handleUploadRevision = useCallback((file: DataRoomFile) => {
    pickerTargetRef.current = {
      folderId: file.folderId,
      supersedesFileId: file.id,
    }
    revisionInputRef.current?.click()
  }, [])

  const handleNoteDraftChange = useCallback((fileId: string, value: string) => {
    setNoteDrafts((prev) => {
      const next = new Map(prev)
      next.set(fileId, value)
      return next
    })
  }, [])

  const discardNoteDraft = useCallback((fileId: string) => {
    setNoteDrafts((prev) => {
      if (!prev.has(fileId)) return prev
      const next = new Map(prev)
      next.delete(fileId)
      return next
    })
  }, [])

  const uploadErrorLabel = useCallback(
    (entry: DataRoomUploadEntry) =>
      translate(
        entry.errorCode
          ? (UPLOAD_ERROR_CODE_KEYS[entry.errorCode] ??
              "DATA_ROOM/ERROR_UPLOAD_FAILED")
          : "DATA_ROOM/ERROR_UPLOAD_FAILED",
      ),
    [translate],
  )

  const handleDownload = useCallback(
    async (file: DataRoomFile) => {
      // Opened synchronously inside the click, before the URL is awaited:
      // a popup opened after an await is blocked by strict browsers.
      const win = window.open("", "_blank")
      if (win) win.opener = null
      if (!win) {
        toast.error(translate("DATA_ROOM/ERROR_DOWNLOAD_FAILED"))
        return
      }
      try {
        win.location.href = await getDataRoomDownloadUrl(file.id)
      } catch {
        win.close()
        toast.error(translate("DATA_ROOM/ERROR_DOWNLOAD_FAILED"))
      }
    },
    [translate],
  )

  const handlePreviewFile = useCallback(
    (file: DataRoomFile) => {
      if (previewKind(file.fileName, file.mimetype) !== "none") {
        setPreviewFile(file)
      } else {
        void handleDownload(file)
      }
    },
    [handleDownload],
  )

  // Folder operations resolve/reject so the dialog can stay open with the
  // draft intact until the change actually went through.
  const folderErrorLabel = useCallback(
    (error: unknown) => {
      const code = getApiErrorCode(error)
      const key = code ? FOLDER_ERROR_CODE_KEYS[code] : undefined
      return translate(key ?? "DATA_ROOM/ERROR_FOLDER_GENERIC")
    },
    [translate],
  )

  const handleCreateFolder = useCallback(
    (name: string, parentId: string | null) =>
      new Promise<void>((resolve, reject) => {
        createFolder(
          { name, parentId },
          {
            onSuccess: () => {
              toast.success(translate("DATA_ROOM/TOAST_FOLDER_CREATED"))
              resolve()
            },
            onError: reject,
          },
        )
      }),
    [createFolder, translate],
  )

  const handleRenameFolder = useCallback(
    (folderId: string, name: string) =>
      new Promise<void>((resolve, reject) => {
        updateFolder(
          { folderId, name },
          {
            onSuccess: () => {
              toast.success(translate("DATA_ROOM/TOAST_FOLDER_RENAMED"))
              resolve()
            },
            onError: reject,
          },
        )
      }),
    [updateFolder, translate],
  )

  const handleDeleteFolder = useCallback(
    (folderId: string) =>
      new Promise<void>((resolve, reject) => {
        deleteFolder(folderId, {
          onSuccess: () => {
            if (selectedFolderId === folderId) setSelectedFolderId(null)
            toast.success(translate("DATA_ROOM/TOAST_FOLDER_DELETED"))
            resolve()
          },
          onError: reject,
        })
      }),
    [deleteFolder, selectedFolderId, setSelectedFolderId, translate],
  )

  const handleConfirmDeleteFile = useCallback(() => {
    if (!fileToDelete) return
    const file = fileToDelete
    // Same rule as the folder dialogs: close only once the change went
    // through, so a failure never leaves the customer staring at a list
    // that still has the file while the dialog already claimed success.
    deleteFile(file.id, {
      onSuccess: () => {
        setFileToDelete((current) => (current?.id === file.id ? null : current))
        toast.success(translate("DATA_ROOM/TOAST_FILE_DELETED"))
      },
      onError: (error) =>
        handleMutationError(
          error,
          DELETE_FILE_ERROR_CODE_KEYS,
          "DATA_ROOM/ERROR_FILE_DELETE_GENERIC",
        ),
    })
  }, [deleteFile, fileToDelete, translate, handleMutationError])

  const handleSaveNote = useCallback(
    (file: DataRoomFile, note: string) => {
      updateFileNote(
        { fileId: file.id, customerNote: note },
        {
          onSuccess: () => {
            // Close only the editor THIS save started for — if the user has
            // since opened a different file's editor while this save was in
            // flight, that session (and its unsaved draft) must stay open.
            setEditingNoteFileId((current) =>
              current === file.id ? null : current,
            )
            discardNoteDraft(file.id)
            toast.success(translate("DATA_ROOM/TOAST_NOTE_SAVED"))
          },
          onError: (error) =>
            handleMutationError(
              error,
              NOTE_ERROR_CODE_KEYS,
              "DATA_ROOM/ERROR_NOTE_FAILED",
            ),
        },
      )
    },
    [updateFileNote, translate, handleMutationError, discardNoteDraft],
  )

  const requestCreate = useCallback(
    (parentId: string | null) => setFolderDialog({ mode: "create", parentId }),
    [],
  )
  const requestRename = useCallback(
    (folder: DataRoomFolder) => setFolderDialog({ mode: "rename", folder }),
    [],
  )
  const requestDelete = useCallback(
    (folder: DataRoomFolder) => setFolderDialog({ mode: "delete", folder }),
    [],
  )

  /* ----------------------------- render ------------------------------ */

  const renderFileList = (
    listFiles: DataRoomFile[],
    listUploads: DataRoomUploadEntry[],
    withFolderNames: boolean,
    variant: "primary" | "secondary" = "primary",
  ) => (
    <DataRoomFileList
      variant={variant}
      files={listFiles}
      uploads={listUploads}
      isLoading={isPending}
      isError={false}
      onRetry={() => refetch()}
      onPreviewFile={handlePreviewFile}
      onDownloadFile={handleDownload}
      onDeleteFile={setFileToDelete}
      onRetryUpload={retry}
      onDismissUpload={dismiss}
      uploadErrorLabel={uploadErrorLabel}
      locale={locale}
      folderNameById={withFolderNames ? folderNameById : undefined}
      currentFolderId={selectedFolderId}
      editingNoteFileId={editingNoteFileId}
      onOpenNoteEditor={(file) => setEditingNoteFileId(file.id)}
      onCloseNoteEditor={() => {
        if (editingNoteFileId) discardNoteDraft(editingNoteFileId)
        setEditingNoteFileId(null)
      }}
      onSaveNote={handleSaveNote}
      isSavingNote={isSavingNote}
      noteDrafts={noteDrafts}
      onNoteDraftChange={handleNoteDraftChange}
      onUploadRevision={handleUploadRevision}
    />
  )

  const sectionHeading = (text: string) => (
    <Typography
      as="h2"
      variant="textXS-bold"
      className="tracking-wide text-grey5 uppercase"
    >
      {text}
    </Typography>
  )

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        data-testid="data-room-upload-input"
        accept={acceptedExtensions.length ? acceptAttribute : undefined}
        onChange={handleFileInputChange}
      />
      <input
        ref={revisionInputRef}
        type="file"
        className="hidden"
        data-testid="data-room-revision-input"
        accept={acceptedExtensions.length ? acceptAttribute : undefined}
        onChange={handleFileInputChange}
      />

      <MainLayout
        contentCenter={
          <div className="mx-auto flex w-full max-w-6xl flex-col gap-L py-2">
            {/* Title */}
            <header className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex min-w-0 flex-1 flex-col gap-3">
                <h1
                  ref={headingRef}
                  tabIndex={-1}
                  className="rounded focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-hidden"
                >
                  <Typography as="span" variant="titleL">
                    {translate("DATA_ROOM/TITLE")}
                  </Typography>
                </h1>
                <Typography variant="textS" className="text-grey5">
                  {translate("DATA_ROOM/HERO_SUBTITLE")}
                </Typography>
                {!isError && (
                  <DataRoomSummary
                    summary={tree?.summary}
                    isLoading={isPending}
                  />
                )}
              </div>
              <Button
                variant="default"
                className="gap-2"
                onClick={openFilePicker}
                disabled={isError || isPending}
                data-testid="data-room-upload-button"
              >
                <IconUpload size={16} />
                {translate("DATA_ROOM/UPLOAD_BUTTON")}
              </Button>
            </header>

            {/* The room could not be loaded: say so, offer nothing that
                would act on a state we cannot see. */}
            {isError && (
              <EmptyState
                title={translate("DATA_ROOM/TABLE_ERROR_TITLE")}
                description={translate("DATA_ROOM/TABLE_ERROR_DESCRIPTION")}
                primaryAction={{
                  label: translate("DATA_ROOM/TABLE_ERROR_RETRY"),
                  onClick: () => refetch(),
                  variant: "outline",
                }}
              />
            )}

            {/* Checklist: the one document that says what is needed */}
            {!isError && isRoot && checklist && (
              <DataRoomChecklistCard
                checklist={checklist}
                buttonRef={checklistRef}
                pulse={checklistPulse}
                onOpen={handlePreviewFile}
                onDownload={handleDownload}
              />
            )}

            {/* Folder header (inside a folder) */}
            {!isError && !isRoot && selectedFolder && (
              <DataRoomFolderHeader
                folder={selectedFolder}
                breadcrumb={breadcrumb}
                hasContent={hasContent(selectedFolder.id)}
                onNavigate={setSelectedFolderId}
                onRequestCreate={requestCreate}
                onRequestRename={requestRename}
                onRequestDelete={requestDelete}
              />
            )}

            {/* Folders */}
            {!isError &&
              (isPending && isRoot ? (
                <div
                  className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4"
                  data-testid="data-room-folder-skeleton"
                >
                  {[0, 1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="min-h-[150px] animate-pulse rounded-xl bg-grey2"
                    />
                  ))}
                </div>
              ) : (
                (visibleFolders.length > 0 || isRoot) && (
                  <DataRoomFolderGrid
                    folders={visibleFolders}
                    parentId={selectedFolderId}
                    fileCountByFolderId={fileCountByFolderId}
                    hasContent={hasContent}
                    onSelect={setSelectedFolderId}
                    onRequestCreate={requestCreate}
                    onRequestRename={requestRename}
                    onRequestDelete={requestDelete}
                    size={isRoot ? "default" : "compact"}
                  />
                )
              ))}

            {/* Root: what turnus sent back, pulled up from every folder */}
            {!isError && isRoot && revisionFiles.length > 0 && (
              <section
                className="flex flex-col gap-2"
                data-testid="data-room-revision-section"
              >
                {sectionHeading(translate("DATA_ROOM/REVISION_SECTION"))}
                {renderFileList(revisionFiles, [], true, "secondary")}
              </section>
            )}

            {/* Root: turnus's templates */}
            {!isError && isRoot && rootTemplates.length > 0 && (
              <section
                className="flex flex-col gap-2"
                data-testid="data-room-templates-section"
              >
                {sectionHeading(translate("DATA_ROOM/TEMPLATES_HEADING"))}
                <Typography variant="textXS" className="text-grey5">
                  {translate("DATA_ROOM/TEMPLATES_INSTRUCTION")}
                </Typography>
                {renderFileList(rootTemplates, [], false, "secondary")}
              </section>
            )}

            {/* Root: the customer's loose files and every running upload */}
            {!isError &&
              isRoot &&
              (rootCustomerFiles.length > 0 || uploadsInView.length > 0) && (
                <section
                  className="flex flex-col gap-2"
                  data-testid="data-room-files-section"
                >
                  {sectionHeading(translate("DATA_ROOM/FILES_WITHOUT_FOLDER"))}
                  {renderFileList(rootCustomerFiles, uploadsInView, true)}
                </section>
              )}

            {/* Inside a folder: one list */}
            {!isError && !isRoot && (hasListContent || isPending) && (
              <div className="flex flex-col gap-2">
                {renderFileList(filesInView, uploadsInView, false)}
                {hasListContent && (
                  <Typography variant="textXS" className="pt-2 text-grey5">
                    {translate("DATA_ROOM/DROP_HINT")}
                  </Typography>
                )}
              </div>
            )}

            {/* Empty folder, or a room with no folders yet */}
            {!isError &&
              (!isRoot || folders.length === 0) &&
              !isPending &&
              !hasListContent && (
                <DataRoomDropzone
                  onPick={openFilePicker}
                  formatsLabel={formatsLabel}
                />
              )}
          </div>
        }
      />

      <DataRoomWelcome
        open={welcome.open}
        sound={welcome.sound}
        checklist={checklist}
        onToggleSound={welcome.toggleSound}
        onDismiss={welcome.dismiss}
        onAfterClose={(openChecklist) => {
          welcome.afterExit()
          const card = checklistRef.current
          if (card && isRoot && !isError) {
            card.focus()
            setChecklistPulse((pulse) => pulse + 1)
          } else {
            headingRef.current?.focus()
          }
          if (openChecklist && checklist) setPreviewFile(checklist)
        }}
      />

      <DropOverlay
        acceptedExtensions={[]}
        hint={[
          selectedFolder
            ? translate("DATA_ROOM/DROP_OVERLAY_HINT_FOLDER", {
                name: selectedFolder.name,
              })
            : translate("DATA_ROOM/DROP_OVERLAY_HINT"),
          formatsLabel,
        ]
          .filter(Boolean)
          .join(" · ")}
        onFilesDropped={welcome.open ? () => {} : handleDroppedFiles}
        onAllFilesDropped={welcome.open ? () => {} : handleDroppedFiles}
        expandDirectories
      />

      <DataRoomUploadTargetDialog
        open={uploadTarget !== null}
        folders={rootFolders}
        onChoose={chooseUploadTarget}
        onClose={() => setUploadTarget(null)}
      />

      {previewFile && (
        <DataRoomPreviewModal
          file={previewFile}
          onClose={() => setPreviewFile(null)}
          onDownload={handleDownload}
        />
      )}

      <DataRoomFolderDialogs
        state={folderDialog}
        onClose={() => setFolderDialog(null)}
        onCreate={handleCreateFolder}
        onRename={handleRenameFolder}
        onDelete={handleDeleteFolder}
        errorLabel={folderErrorLabel}
      />

      <Dialog
        open={fileToDelete !== null}
        onOpenChange={(open) => !open && setFileToDelete(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <IconAlertTriangle size={18} className="text-warning" />
              {translate("DATA_ROOM/DELETE_FILE_TITLE")}
            </DialogTitle>
          </DialogHeader>
          <Typography variant="textS" className="text-grey5">
            {translate("DATA_ROOM/DELETE_FILE_DESCRIPTION", {
              name: fileToDelete?.fileName ?? "",
            })}
          </Typography>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFileToDelete(null)}>
              {translate("DATA_ROOM/FOLDER_DIALOG_CANCEL")}
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDeleteFile}
              disabled={isDeletingFile}
            >
              {translate("DATA_ROOM/DELETE_FILE_CONFIRM")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
